import React, { Component, useState, useEffect } from "react";
import { styled, useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Check from '@mui/icons-material/Check';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';

import { UserInfo, netGet, netPost, netFetch, keycloak } from "../network";

function EditGroupDialog(props) {
    const {
        title = 'Edit Effectivity Group',
        group,
        open,
        onSave,
        onCancel,
        ...other
    } = props;

    // const [content, setContent] = useState('');
    const [okEnabled, setOkEnabled] = useState(false);
    const [description, setDescription] = useState('');
    
    useEffect(() => {
        // setContent('');
        setDescription('');
        if ( open && group) {
            setOkEnabled(false);
            setDescription(group.label);
        }
    }, [open]);

    const onCancelClick = () => {
        onClose();
    };

    const onClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };


    const onOKClick = () => {
        if (typeof onSave === 'function') {
            onSave({
                group: group,
                description: description,
            });
        }
    }

    const handleDescriptionChange = (event) => {
        setDescription(event.target.value);
        setOkEnabled(true);
    }

    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 600,
                        maxWidth: 600,
                        maxHeight: 550,
                        minHeight: 400,
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent>
                <TextField label="Effectivity Group Name" value={description} onChange={handleDescriptionChange} fullWidth size="small" sx={{marginTop: 2}} 
                    multiline rows="3" />
            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1}}>
                <Button variant="outlined" onClick={() => onOKClick()} sx={{ minWidth: '7em' }} disabled={!okEnabled} >Save</Button>
                <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', marginLeft: 2 }}>Cancel</Button>
            </DialogActions>
        </Dialog>

    );

}

function EffectivityEditor (props) {
    const {
        namespace='NZ_AIRCRAFT',
        onModelChange,
        ...other
    } = props;

    const [treeModel, setTreeModel] = useState([]);
    const [groupList, setGroupList] = useState([]);
    const [cellMap, setCellMap] = useState({});  // id to index
    const [acList, setAcList] = useState([]);
    const [editState, setEditState] = useState({});
    const [changedState, setChangedState] = useState({});
    const [edits, setEdits] = useState({});
    const [dirty, setDirty] = useState(false);
    const [colDirty, setColDirty] = useState(false);
    const [selectedAc, setSelectedAc] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [menuIndex, setMenuIndex] = React.useState(1);
    const [colMenuIndex, setColMenuIndex] = useState(0);
    const [contextMenu, setContextMenu] = React.useState(null);
    const [contextColMenu, setContextColMenu] = React.useState(null);
    const [noticeOpen, setNoticeOpen] = useState(false);
    const [noticeMessage, setNoticeMessage] = useState('');
    const [editGroupDialogOpen, setEditGroupDialogOpen] = useState(false);
    // const open = Boolean(anchorEl);
    const mid = React.useId();
    const menuId = `${mid}-menu`;
    const colMenuId = `${mid}-col-menu`;

    useEffect(() => {
        setEditState({});
        setChangedState({});
        setSelectedAc(null);
        setSelectedGroup(null);
        setEdits({});
        setDirty(false);
        loadData();
    },[namespace]);

    const loadGroupData = () => {
        netGet('/api/eff/tree?ns=' + encodeURIComponent(namespace))
            .then(response => response.json())
            .then(etree => {
                if (Array.isArray(etree)) {
                    setTreeModel(etree);
                    const elist = [];
                    const emap = {};
                    loadCellMap(etree, elist, emap);
                    setCellMap(emap);
                    setGroupList(elist);
                    resolveTableHeaders(etree);
                    if (typeof onModelChange === 'function') {
                        onModelChange(etree);
                    }
                }
            }).catch(error => {
                console.log('Error fetching Effectivity tree: ' + error);
            });
    };

    const loadAircraftData = () => {
        netGet('/api/eff/aircraft?ns=' + encodeURIComponent(namespace))
            .then(response => response.json())
            .then( elist => {
                if ( Array.isArray(elist) ) {
                    setAcList(elist);
                }
            }).catch(error => {
                console.log('Error fetching aircraft: ' + error);
            });
    };

    const loadData = () => {
        loadGroupData();
        loadAircraftData();
    };

    const loadCellMap = (etree, elist, emap) => {
        etree.forEach( eff => {
            if (eff.type === 'group') {
                if (Array.isArray(eff.children) && eff.children.length > 0) {
                    loadCellMap(eff.children, elist, emap);
                } else {
                    emap[eff.id] = elist.length;
                    elist.push({ id: eff.id, eff: eff });
                }
            }
        });
    };

    const loadRows = (rows, ri, children, pindex) => {
        // generate rows of sibling cells based on the tree level regardless of parent
        // but keep track of original parent
        if (rows.length <= ri) {
            rows.push({
                index: ri,
                cells: [],
            });
        }
        let colspan = 1;
        for(let i=0; i<children.length; i++) {
            const eff = children[i];
            if (eff.type === 'group') {
                const row = rows[ri];
                const cell = {
                    label: eff.name,
                    eff: eff,
                    index: row.cells.length,
                    pindex: pindex,
                    colspan: 1,
                    rowspan: 1,
                    leaf: false,
                };

                if (Array.isArray(eff.children) && eff.children.length > 0) {
                    loadRows(rows, ri + 1, eff.children, cell.index);
                } else {
                    cell.leaf = true;
                }
                row.cells.push(cell);
            }
        }
        return colspan;
    };

    const resolveColSpans = (rows) => {
        // accumulate spans from the bottom up using the parent index to track columns
        const cm = {};
        for(let i=rows.length - 1; i >= 0; i--) {
            const row = rows[i];
            
            for(let j=0; j<row.cells.length; j++) {
                const cell = row.cells[j];                
                if ( i < rows.length - 1 && !cell.leaf ) {
                    const nrow = rows[i+1];
                    let span = 0;
                    for(let k=0; k<nrow.cells.length; k++) {
                        const ncell = nrow.cells[k];
                        if ( cell.index === ncell.pindex ) {
                            span = span + ncell.colspan;
                        }
                    }
                    cell.colspan = cell.colspan + span - 1;
                }
                if ( cell.leaf ) {
                    cm[j] = cell;
                }
            }
        }
        // setCellMap(cm);
    };

    const resolveRowSpans = (rows) => {
        // span rows if leaf nodes are not in the last row
        for(let i=0; i<rows.length; i++) {
            const row = rows[i];
            for(let j=0; j<row.cells.length; j++) {
                const cell = row.cells[j];
                if ( cell.leaf ) {
                    cell.rowspan = rows.length - i;
                }
            }
        }
    };

    

    const resolveTableHeaders = (etree) => {
        const rows = [];
        let ri = 0;
        loadRows(rows, ri, etree, -1);
        // console.log('rows: ' + JSON.stringify(rows));
        resolveColSpans(rows);
        resolveRowSpans(rows);
        return (
            <TableHead sx={{position: 'sticky', top: 0, zIndex: 400}}>
                {rows.map( (row, rindex) =>
                    <TableRow sx={{height: 'min-content'}}>
                        {rindex === 0 &&
                            <TableCell
                                align="center"
                                style={{
                                    position: 'sticky',
                                    top: 0,
                                    left: 0,
                                    backgroundColor: 'white',
                                    zIndex: 500,
                                    width: 60,
                                    padding: 0,
                                    border: '1px solid rgba(224,224,224,1)',
                                    
                                    verticalAlign: 'middle',
                                }} rowSpan={rows.length}>
                                <Typography sx={{
                                    writingMode: 'vertical-rl', // For vertical text flow
                                    textOrientation: 'sideways', // To rotate individual letters 90 degrees
                                    transform: 'rotate(180deg)', // To orient the text as desired (e.g., bottom-to-top)
                                    // whiteSpace: 'nowrap', // Prevent text wrapping

                                    // backgroundColor: '#d0d0d0',
                                    maxHeight: '300px',
                                    display: 'inline-block',
                                    lineHeight: '1.05'
                                }}>Tail Number</Typography>
                            </TableCell>
                        }
                        {rindex === 0 &&
                            <TableCell
                                align="center"
                                style={{
                                    position: 'sticky',
                                    left: 61,
                                    top: 0,
                                    backgroundColor: 'white',
                                    zIndex: 500,
                                    width: 60,
                                    padding: 0,
                                    borderTop: '1px solid rgba(224,224,224,1)',
                                    borderRight: '1px solid rgba(224,224,224,1)',
                                    verticalAlign: 'middle'
                                }} rowSpan={rows.length}>
                                <Typography sx={{
                                    writingMode: 'vertical-rl', // For vertical text flow
                                    textOrientation: 'sideways', // To rotate individual letters 90 degrees
                                    transform: 'rotate(180deg)', // To orient the text as desired (e.g., bottom-to-top)
                                    // whiteSpace: 'nowrap', // Prevent text wrapping

                                    // backgroundColor: '#d0d0d0',
                                    maxHeight: '300px',
                                    display: 'inline-block',
                                    lineHeight: '1.05'
                                }}>Manufacturer Serial Number</Typography>
                            </TableCell>
                        }
                        {row.cells.map( cell =>
                            <TableCell colSpan={cell.colspan} rowSpan={cell.rowspan}
                                align="center" 
                                sx={{
                                    border: '1px solid rgba(224,224,224,1)', 
                                    maxHeight: cell.leaf ? '300px' : 'min-content',
                                    padding: '5px',
                                    paddingLeft: cell.leaf ? '14px' : '5px',
                                    paddingRight: cell.leaf ? '14px' : '5px',
                                }}
                                onContextMenu={(event) => handleContextColMenu(event, cell)}
                            >
                                { cell.leaf ? 
                                    <Typography sx={{
                                        writingMode: 'vertical-rl', // For vertical text flow
                                        textOrientation: 'sideways', // To rotate individual letters 90 degrees
                                        transform: 'rotate(180deg)', // To orient the text as desired (e.g., bottom-to-top)
                                        // whiteSpace: 'nowrap', // Prevent text wrapping
                                        
                                        // backgroundColor: '#d0d0d0',
                                        maxHeight: '300px',
                                        display: 'inline-block',
                                        lineHeight: '1.05'
                                    }}>{cell.label}</Typography> :
                                    <Typography sx={{
                                        lineHeight: '1.05',
                                        display: 'inline-block'
                                    }}
                                    >{cell.label}</Typography>
                                }
                            </TableCell>
                        )}
                    </TableRow>
                )}
            </TableHead>
        );
    };

    const handleClick = (event, ac, sindex) => {
        setAnchorEl(event.currentTarget);
        setMenuIndex(sindex);
        // console.log('handle click...');
    };
    
    const handleClose = () => {
        // setAnchorEl(null);
        setContextMenu(null);
        setSelectedAc(null);
        setSelectedGroup(null);
    };

    const handleColClose = () => {
        // setAnchorEl(null);
        setContextColMenu(null);
        setSelectedGroup(null);
    };

    const handleMenuClick = (mindex) => {
        setContextMenu(null);
        const ac = selectedAc;
        const gid = selectedGroup;

        if ( ac && gid ) {
            const key = `${gid}_${ac.id}`;
            const v = ac.groupIds?.includes(gid);
            const es = {...editState};
            const cs = {...changedState};
            const vedits = {...edits};
            if ( mindex === 0  ) {
                if ( v ) {
                    es[key] = 0;
                    cs[key] = true;
                    if (! vedits[ac.id]) {
                        vedits[ac.id] = {};
                        vedits[ac.id][gid] = 0;
                    } else {
                        vedits[ac.id][gid] = 0;
                    }
                    setDirty(true);
                } else {
                    if ( vedits[ac.id] ) {
                        vedits[ac.id][gid] = undefined;
                    }
                    es[key] = undefined;
                    cs[key] = false;
                }
            }
            if ( mindex === 1 ) {
                if ( v ) {
                    es[key] = undefined;
                    cs[key] = false;
                    if ( vedits[ac.id] ) {
                        vedits[ac.id][gid] = undefined;
                    }
                } else {
                    es[key] = 1;
                    cs[key] = true;
                    if (! vedits[ac.id]) {
                        vedits[ac.id] = {};
                        vedits[ac.id][gid] = 1;
                    } else {
                        vedits[ac.id][gid] = 1;
                    }
                    setDirty(true);
                }
            }
            setEditState(es);
            setChangedState(cs);
            setEdits(vedits);
        }
    };

    const handleContextMenu = (event, sindex, ac, gid) => {
        event.preventDefault();
        setMenuIndex(sindex);
        setSelectedAc(ac);
        setSelectedGroup(gid);

        setContextMenu(
            contextMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                }
                : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                // Other native context menus might behave different.
                // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                null,
        );
    };

    const handleSave = () => {
        
        console.log('Save data: ' + JSON.stringify(edits));
        if (dirty) {
            netPost('/api/eff/effgroup?ns=' + encodeURIComponent(namespace), edits)
                .then(resp => {
                    if (!resp.ok) {
                        console.log('Error saving effectivity: ' + resp.status);
                    } else {
                        setSelectedAc(null);
                        setSelectedGroup(null);
                        setEditState({});
                        setChangedState({});
                        setEdits({});
                        setDirty(false);
                        loadAircraftData();
                        setNoticeMessage('Effectivity associations saved successfully.')
                        setNoticeOpen(true);
                    }
                }).catch(error => {
                    console.log('Error saving effectivity: ' + error);
                });
        }
        if ( colDirty ) {
            setColDirty(false);
            // loadData();
            setNoticeMessage('Effectivity groups saved successfully.')
            setNoticeOpen(true);
        }
    };

    const handleNoticeClose = () => {
        setNoticeOpen(false);
        setNoticeMessage('');
    };

    const handleContextColMenu = (event, group) => {
        event.preventDefault();
        setSelectedGroup(group);
        // console.log('Edited group: ' + JSON.stringify(group));
        setContextColMenu(
            contextColMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                }
                : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                // Other native context menus might behave different.
                // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                null,
        );
    };

    const handleColMenuClick = (mindex) => {
        setContextColMenu(null);
        if ( mindex == 0 ) {
            setEditGroupDialogOpen(true);
        }
    };

    const handleEditGroupSave = (data) => {
        setEditGroupDialogOpen(false);
        console.log('Edited group: ' + JSON.stringify(data));
        const g = data?.group?.eff?.group;
        console.log('Saving group ' + JSON.stringify(g));
        if ( g && data.description ) {
            g.description = data.description;
            netPost('/api/eff/group', g)
                .then(response => {
                    if ( !response.ok ) {
                        console.log('Failed to save group ' + data?.group?.label + ':  ' + response.status);
                    } else {
                        setColDirty(true);
                        loadGroupData();
                    }
                }).catch(error => {
                    console.log('Error saving group: ' + error);
                });
        }
    };

    // 'calc(100% - 70px)'
    // onClick={(event) => handleClick(event, ac, sindex)}
    // anchorEl={anchorEl}
    // open={open}

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', position: 'relative', pt: 2, width: '100%', height: '100%'}}>
            <Box sx={{display: 'flex', justifyContent: 'flex-end', pr: 4 }}>
                <Button variant="outlined" disabled={!(dirty || colDirty)} onClick={handleSave}>Save</Button>
            </Box>
            <Box sx={{ width: '100%', height: 'calc(100% - 3em)', padding: 2, position: 'relative', flexGrow: 1 }}>
                <TableContainer sx={{ maxHeight: '100%' }}>
                    <Table stickyHeader sx={{ borderCollapse: 'separate' }}>
                        {resolveTableHeaders(treeModel)}
                        <TableBody>
                            {Array.isArray(acList) && acList.map((ac, aindex) => {
                                if (aindex === 0) {
                                    // console.log('ACg: ' + JSON.stringify(ac.groupIds));
                                    // console.log('groupList: ' + JSON.stringify(groupList));
                                }
                                return (<TableRow style={{ backgroundColor: 'white' }}>
                                    <TableCell
                                        align="center"
                                        style={{
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: 'white',
                                            zIndex: 10,
                                            width: 60,
                                            borderLeft: '1px solid rgba(224,224,224,1)',
                                            borderRight: '1px solid rgba(224,224,224,1)',
                                        }}>
                                        {ac.label}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        style={{
                                            position: 'sticky',
                                            left: 60,
                                            backgroundColor: 'white',
                                            zIndex: 10,
                                            width: 60,
                                            borderRight: '1px solid rgba(224,224,224,1)',
                                        }}>
                                        {ac.msn}
                                    </TableCell>
                                    {Array.isArray(groupList) && groupList.map((g, gindex) => {
                                        const gid = g?.eff?.group?.id;
                                        const sindex = ac.groupIds.includes(gid) ? 1 : 0;
                                        const key = `${gid}_${ac.id}`;
                                        const v = changedState[key] ? editState[key] : sindex;
                                        return (
                                            <TableCell
                                                align="center"
                                                style={{
                                                    borderRight: '1px solid rgba(224,224,224,1)',
                                                    cursor: 'context-menu',
                                                    color: changedState[key] ? 'rgba(255,0,33,0.9)' : 'rgba(0,0,0,0.75)',
                                                    fontWeight: changedState[key] ? 'bold': 'normal',
                                                }}

                                                onContextMenu={event => handleContextMenu(event, v, ac, gid)}
                                            >
                                                {v === 1 ? '1' : '0'}
                                            </TableCell>);
                                    })
                                    }
                                </TableRow>);
                            })

                            }

                        </TableBody>
                    </Table>
                </TableContainer>
                <Menu
                    id={menuId}
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextMenu !== null
                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                            : undefined
                    }
                >
                    <MenuItem onClick={() => handleMenuClick(0)} selected={menuIndex === 0}>
                        {menuIndex === 0 ?
                            <>
                                <ListItemIcon>
                                    <Check />
                                </ListItemIcon><ListItemText>0</ListItemText> </> :
                            <ListItemText inset>0</ListItemText>
                        }
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuClick(1)} selected={menuIndex === 1}>
                        {menuIndex === 1 ?
                            <>
                                <ListItemIcon>
                                    <Check />
                                </ListItemIcon><ListItemText>1</ListItemText> </> :
                            <ListItemText inset>1</ListItemText>
                        }
                    </MenuItem>
                </Menu>
                <Menu
                    id={colMenuId}
                    open={contextColMenu !== null}
                    onClose={handleColClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextColMenu !== null
                            ? { top: contextColMenu.mouseY, left: contextColMenu.mouseX }
                            : undefined
                    }
                >
                    <MenuItem onClick={() => handleColMenuClick(0)} >
                        <ListItemText>Edit...</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleColMenuClick(1)}>
                        <ListItemText>Insert Before...</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleColMenuClick(2)}>
                        <ListItemText>Insert After...</ListItemText>
                    </MenuItem>
                </Menu>
                <Snackbar
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    open={noticeOpen}
                    autoHideDuration={5000}
                    onClose={handleNoticeClose}
                    message={noticeMessage}
                />
                <EditGroupDialog open={editGroupDialogOpen} group={selectedGroup} onCancel={() => setEditGroupDialogOpen(false)}
                    onSave={handleEditGroupSave} />
        </Box>
        </Box>
    );
}

export {EffectivityEditor};